<script setup lang="ts">
import type { Package, Sport } from '~/types'
import * as z from 'zod'

const toast = useToast()
const loading = ref(false)

const schema = z.object({
  title: z.string().min(2, 'Troppo corto'),
  message: z.string().min(10, 'Scrivi almeno 10 caratteri'),
  email: z.email('Inserisci un’email valida'),
  firstname: z.string().min(2, 'Troppo corto'),
  lastname: z.string().min(2, 'Troppo corto'),
  sportId: z.number().optional(),
  packageId: z.number().optional()
})

type Schema = z.output<typeof schema>

const initialState: Partial<Schema> = {
  title: undefined,
  message: undefined,
  email: undefined,
  firstname: undefined,
  lastname: undefined,
  sportId: undefined,
  packageId: undefined
}

const state = reactive<Partial<Schema>>({ ...initialState })

const { data: sportList } = await useFetch('/api/sports')
const { data: packageList } = await useFetch('/api/packages')
const sports = computed(() => (sportList.value?.sports || []) as Sport[])
const packages = computed(() => (packageList.value?.tiers || []) as Package[])

async function onSubmit() {
  loading.value = true
  try {
    await $fetch('/api/messages', {
      method: 'POST',
      body: state
    })

    toast.add({
      title: 'Messaggio inviato',
      description: 'Ti risponderò il prima possibile.',
      color: 'success'
    })

    // Reset corretto dello stato del form
    Object.assign(state, initialState)
  } catch (err) {
    console.error(err)
    toast.add({
      title: 'Errore',
      description: 'Errore nell’invio del messaggio. Riprova tra poco.',
      color: 'error'
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section
    id="contacts"
    class="relative overflow-hidden border-t border-slate-200/70 bg-slate-50 dark:border-slate-800 dark:bg-slate-950"
  >
    <!-- Decorazioni soft -->
    <div class="pointer-events-none absolute inset-0">
      <div class="absolute -top-20 left-1/2 h-56 w-[38rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      <div class="absolute -bottom-24 right-0 h-56 w-56 rounded-full bg-sky-500/10 blur-3xl" />
    </div>

    <UContainer class="relative py-14 md:py-20">
      <div class="mx-auto max-w-2xl text-center">
        <h2 class="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white md:text-4xl">
          Contattami
        </h2>
        <p class="mt-3 text-slate-600 dark:text-slate-300">
          Raccontami cosa ti serve: ti consiglio il percorso più adatto e ti rispondo rapidamente.
        </p>
      </div>

      <div class="mt-10 grid gap-6 lg:grid-cols-12 lg:items-start">
        <!-- Colonna info (in linea “landing”) -->
        <div class="lg:col-span-5">
          <div
            class="rounded-2xl border border-slate-200/70 bg-white/70 p-6 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/40"
          >
            <h3 class="text-base font-semibold text-slate-900 dark:text-white">
              Cosa succede dopo?
            </h3>
            <ul class="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  1
                </span>
                <span>Leggo la richiesta e, se serve, ti faccio 1–2 domande per capire l’obiettivo.</span>
              </li>
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  2
                </span>
                <span>Ti propongo sport/pacchetto più coerenti (se non li hai già selezionati).</span>
              </li>
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  3
                </span>
                <span>Se tutto torna, fissiamo i prossimi step.</span>
              </li>
            </ul>

            <div
              class="mt-6 rounded-xl border border-slate-200/70 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40"
            >
              <p class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Tip
              </p>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Più dettagli metti nel messaggio (obiettivo, livello, disponibilità), più la risposta sarà precisa.
              </p>
            </div>
          </div>
        </div>

        <!-- Colonna form (card pulita, coerente) -->
        <div class="lg:col-span-7">
          <div
            class="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/40"
          >
            <UForm
              :schema="schema"
              :state="state"
              class="space-y-5"
              @submit="onSubmit"
            >
              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField label="Nome" name="firstname">
                  <template #label>
                    <span>Nome <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.firstname"
                    placeholder="Es. Marco"
                    icon="i-lucide-user"
                    class="w-full"
                  />
                </UFormField>

                <UFormField name="lastname">
                  <template #label>
                    <span>Cognome <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.lastname"
                    placeholder="Es. Rossi"
                    icon="i-lucide-user"
                    class="w-full"
                  />
                </UFormField>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField name="email">
                  <template #label>
                    <span>Email <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.email"
                    type="email"
                    placeholder="nome@dominio.com"
                    icon="i-lucide-mail"
                    class="w-full"
                  />
                </UFormField>

                <UFormField name="title">
                  <template #label>
                    <span>Oggetto <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.title"
                    placeholder="Es. Info coaching / Piano gare"
                    icon="i-lucide-tag"
                    class="w-full"
                  />
                </UFormField>
              </div>

              <UFormField name="message">
                <template #label>
                  <span>Messaggio <span class="text-red-500">*</span></span>
                </template>
                <UTextarea
                  v-model="state.message"
                  :rows="6"
                  placeholder="Scrivi qui la tua richiesta, obiettivo e contesto..."
                  class="w-full"
                />
              </UFormField>

              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField label="Sport" name="sportId">
                  <USelect
                    v-model="state.sportId"
                    :items="sports"
                    label-key="name"
                    value-key="id"
                    placeholder="Seleziona uno sport"
                    class="w-full"
                  />
                </UFormField>

                <UFormField label="Pacchetto" name="packageId">
                  <USelect
                    v-model="state.packageId"
                    :items="packages"
                    label-key="title"
                    value-key="id"
                    placeholder="Seleziona un pacchetto"
                    class="w-full"
                  />
                </UFormField>
              </div>

              <div class="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
                <UButton
                  label="Invia messaggio"
                  color="primary"
                  variant="solid"
                  type="submit"
                  :loading="loading"
                  icon="i-lucide-send"
                  class="sm:w-auto w-full"
                />
              </div>

              <p class="text-xs text-slate-500 dark:text-slate-400">
                Inviando questo form accetti che io possa ricontattarti via email.
              </p>
            </UForm>
          </div>
        </div>
      </div>
    </UContainer>
  </section>
</template>

<style scoped>

</style>
